import { zodResolver } from '@hookform/resolvers/zod';
import { passwordIssues } from '@financas/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useAuth } from '../../app/auth-context';
import { ApiError, api } from '../../lib/api';

/**
 * Primeiro acesso. Cria a conta dona e o household; depois disso a rota se
 * fecha sozinha no servidor e novas pessoas so entram por convite.
 *
 * A politica de senha e validada aqui com a MESMA funcao do servidor
 * (@financas/shared), para o retorno ser imediato — mas quem decide continua
 * sendo o servidor.
 */

const schema = z
  .object({
    name: z.string().trim().min(2, 'Informe seu nome.'),
    email: z.string().trim().min(1, 'Informe o e-mail.').email('E-mail invalido.'),
    householdName: z.string().trim().min(2, 'De um nome para a casa.'),
    password: z.string(),
    confirmPassword: z.string(),
  })
  .superRefine((values, ctx) => {
    for (const issue of passwordIssues(values.password, {
      email: values.email,
      name: values.name,
    })) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['password'], message: issue });
    }
    if (values.password !== values.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['confirmPassword'],
        message: 'As senhas nao conferem.',
      });
    }
  });

type FormValues = z.infer<typeof schema>;

export function BootstrapPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      householdName: 'Minha casa',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const result = await api.bootstrap({
        name: values.name,
        email: values.email,
        password: values.password,
        householdName: values.householdName,
      });
      signIn(result.user, result.accessToken);
      navigate('/seguranca?primeiro-acesso=1', { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Nao foi possivel conectar ao servidor.',
      );
    }
  });

  const errors = form.formState.errors;

  return (
    <div className="auth-screen">
      <main className="auth-card">
        <h1>Primeiro acesso</h1>
        <p className="subtitle">
          Esta conta sera a dona do sistema. Depois dela, novos acessos so por convite.
        </p>

        {error && (
          <div className="alert error" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={onSubmit} noValidate>
          <div className="field">
            <label htmlFor="name">Seu nome</label>
            <input id="name" autoComplete="name" autoFocus {...form.register('name')} />
            {errors.name && <span className="field-error">{errors.name.message}</span>}
          </div>

          <div className="field">
            <label htmlFor="email">E-mail</label>
            <input id="email" type="email" autoComplete="username" {...form.register('email')} />
            {errors.email && <span className="field-error">{errors.email.message}</span>}
          </div>

          <div className="field">
            <label htmlFor="householdName">Nome da casa</label>
            <input id="householdName" {...form.register('householdName')} />
            <span className="field-hint">
              Usado para separar as financas se um dia outra pessoa entrar.
            </span>
            {errors.householdName && (
              <span className="field-error">{errors.householdName.message}</span>
            )}
          </div>

          <div className="field">
            <label htmlFor="password">Senha</label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              aria-invalid={Boolean(errors.password)}
              {...form.register('password')}
            />
            <span className="field-hint">
              Ao menos 12 caracteres, com variedade. Frases longas funcionam bem.
            </span>
            {errors.password && <span className="field-error">{errors.password.message}</span>}
          </div>

          <div className="field">
            <label htmlFor="confirmPassword">Confirme a senha</label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              {...form.register('confirmPassword')}
            />
            {errors.confirmPassword && (
              <span className="field-error">{errors.confirmPassword.message}</span>
            )}
          </div>

          <button className="btn block" type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Criando...' : 'Criar conta'}
          </button>
        </form>
      </main>
    </div>
  );
}
